import React, { Component } from 'react';
import { Table, Spinner, Badge, Button } from 'react-bootstrap';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { API } from '../../services/API';
import { FaSort, FaSortUp, FaSortDown, FaCalculator } from 'react-icons/fa';
import { getTranslate, LANGUAGE_KEYS } from '../../utils/language';
import { getToken } from '../../utils/token';
import { getApiUrl } from '../../utils/platform';
import moment from 'moment';

class QuantityChangeModal extends Component {
  state = {
    loading: false,
    changes: [],
    sortBy: 'created_at',
    sortOrder: -1,
    recalculating: false
  };

  componentDidMount() {
    this.loadQuantityChanges();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.productCode !== this.props.productCode || 
        (!prevProps.show && this.props.show)) {
      this.loadQuantityChanges();
    }
  }

  loadQuantityChanges = async () => {
    const { productCode } = this.props;
    console.log("QuantityChangeModal productCode", productCode);
    if (!productCode) return;

    this.setState({ loading: true });
    try {
      const response = await API.get(`${getApiUrl()}/api/ses-product-qty-change/product/${productCode}`);
      const changes = response.data.data || [];
      this.setState({ changes: this.sortChanges(changes) });
    } catch (error) {
      console.error('Error loading quantity changes:', error);
    } finally {
      this.setState({ loading: false });
    }
  };

  handleSort = () => {
    this.setState(prevState => ({
      sortOrder: -prevState.sortOrder
    }), () => {
      this.setState(prevState => ({
        changes: this.sortChanges(prevState.changes)
      }));
    });
  };

  sortChanges = (changes) => {
    const { sortOrder } = this.state;
    return [...changes].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder * (dateA - dateB);
    });
  };

  handleRecalculate = async () => {
    const { productCode } = this.props;
    if (!productCode) return;

    if (window.confirm('Are you sure you want to recalculate inventory quantities for this product?')) {
      this.setState({ recalculating: true });
      const token = getToken();
      
      try {
        const response = await fetch(`${getApiUrl()}/api/ses-product-qty-change/recalculate?&product_code=${productCode}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        alert('Recalculation completed successfully!');
        
        // Reload the quantity changes to show updated data
        this.loadQuantityChanges();
      } catch (error) {
        console.error('Error recalculating inventory:', error);
        alert('Failed to recalculate inventory. See console for details.');
      } finally {
        this.setState({ recalculating: false });
      }
    }
  };

  render() {
    const { show, onHide, productCode, productName, showCalculateButton = false } = this.props;
    const { loading, changes, sortOrder, recalculating } = this.state;

    return (
      <Modal isOpen={show} toggle={onHide} size="xl" style={{ maxWidth: '90vw', margin: '0 auto' }}>
        <ModalHeader toggle={onHide}>
          <div className="d-flex align-items-center">
            <div>
              {getTranslate(LANGUAGE_KEYS.TEXT_QUANTITY_CHANGE_HISTORY_FOR)} {productCode}
              {productName && ` (${productName})`}
            </div>
            {showCalculateButton && (
              <Button
                variant="primary"
                size="sm"
                onClick={this.handleRecalculate}
                disabled={recalculating}
                title="Recalculate Inventory"
                className="ms-3"
              >
                {recalculating ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Recalculating...
                  </>
                ) : (
                  <>
                    <FaCalculator /> Recalculate
                  </>
                )}
              </Button>
            )}
          </div>
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">{getTranslate(LANGUAGE_KEYS.TEXT_LOADING)}</span>
            </div>
          ) : (
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th 
                    onClick={this.handleSort} 
                    style={{ cursor: 'pointer' }}
                  >
                    {getTranslate(LANGUAGE_KEYS.TEXT_DATE)} {sortOrder < 0 ? <FaSortDown /> : <FaSortUp />}
                  </th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_TYPE)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_CHANGE_AMOUNT)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_NEW_QUANTITY)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_CELL_CODE)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_UPDATED_BY)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_SYSTEM_NOTE)}</th>
                  <th>{getTranslate(LANGUAGE_KEYS.TEXT_STAFF_NOTE)}</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change, index) => (
                  <tr key={index}>
                    <td>{moment(change.created_at).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td>
                      <Badge bg={
                        change.type === 'IMPORT' ? 'success' : 
                        change.type === 'EXPORT' ? 'danger' : 
                        change.type === 'INVENTORY_CHECK' ? 'warning' : 'secondary'
                      }>
                        {getTranslate(LANGUAGE_KEYS[`TEXT_QUANTITY_CHANGE_TYPE_${change.type}`]) || change.type}
                      </Badge>
                    </td>
                    <td className={change.quantity_change > 0 ? 'text-success' : 'text-danger'}>
                      {change.quantity_change > 0 ? '+' : ''}{change.quantity_change}
                    </td>
                    <td>{change.quantity}</td>
                    <td>{change.warehouse_cell_code}</td>
                    <td>{change.updated_by}</td>
                    <td>{change.system_note || '-'}</td>
                    <td>{change.staff_note || '-'}</td>
                  </tr>
                ))}
                {changes.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center">{getTranslate(LANGUAGE_KEYS.TEXT_NO_QUANTITY_CHANGES)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>
    );
  }
}

export default QuantityChangeModal; 